import { alertController, toastController } from "@ionic/vue";
import {
  decryptPassword, encryptPassword, passphraseKeySource, sessionKeySource, unlockOf,
  type EncryptedPassword, type KeySource, type UnlockMode,
} from "@/helper/passwordCrypto";
import { loadMaster } from "@/helper/passwordKeyStore";

async function notify(message: string): Promise<void> {
  const toast = await toastController.create({ message, duration: 4000, color: "warning" });
  await toast.present();
}

/** Asks for text with password inputs, `null` if the dialog was cancelled. Nothing typed is logged or kept. */
async function askSecrets(header: string, message: string, fields: { name: string; placeholder: string }[]): Promise<Record<string, string> | null> {
  const alert = await alertController.create({
    header,
    message,
    inputs: fields.map((field) => ({ name: field.name, type: "password" as const, placeholder: field.placeholder, attributes: { autocomplete: "off" } })),
    buttons: [{ text: "Cancel", role: "cancel" }, { text: "OK", role: "confirm" }],
  });
  await alert.present();
  const { role, data } = await alert.onDidDismiss();
  if (role !== "confirm") return null;
  return data?.values ?? null;
}

/** The key source of a mode: the stored master for `session`, the typed passphrase for `every_access`. `null` if there is none or it was cancelled. */
async function keySourceFor(unlock: UnlockMode, header: string): Promise<KeySource | null> {
  if (unlock === "session") {
    const master = await loadMaster();
    if (master === null) {
      await notify("This password is unlocked by your login, but this browser has no key yet. Log in again.");
      return null;
    }
    return sessionKeySource(master);
  }
  const typed = await askSecrets(header, "Passphrase of this password", [{ name: "passphrase", placeholder: "Passphrase" }]);
  return typed?.passphrase ? passphraseKeySource(typed.passphrase) : null;
}

/**
 * Asks for a new password (and for the passphrase of an `every_access` one, twice) and returns it encrypted.
 * `null` if cancelled, empty or the passphrases differ.
 */
export async function askAndEncryptPassword(unlock: UnlockMode): Promise<EncryptedPassword | null> {
  const fields = [{ name: "password", placeholder: "Password" }];
  if (unlock === "every_access") {
    fields.push({ name: "passphrase", placeholder: "Passphrase" }, { name: "repeat", placeholder: "Repeat the passphrase" });
  }
  const typed = await askSecrets(
    "Set password",
    unlock === "session" ? "It is encrypted with the key of your login." : "It is encrypted with a passphrase that is asked on every access.",
    fields,
  );
  if (!typed?.password) return null;
  if (unlock === "every_access") {
    if (!typed.passphrase || typed.passphrase !== typed.repeat) {
      await notify("The passphrases are empty or differ.");
      return null;
    }
    return encryptPassword(typed.password, unlock, passphraseKeySource(typed.passphrase));
  }
  const keySource = await keySourceFor(unlock, "");
  return keySource === null ? null : encryptPassword(typed.password, unlock, keySource);
}

/** The plain password of a stored value, `null` if it was cancelled, the key is missing or does not fit. */
export async function askAndDecryptPassword(value: unknown): Promise<string | null> {
  const unlock = unlockOf(value);
  if (unlock === null) return null;
  const keySource = await keySourceFor(unlock, "Unlock password");
  if (keySource === null) return null;
  try {
    return await decryptPassword(value as EncryptedPassword, keySource);
  } catch {
    await notify(unlock === "session" ? "Could not decrypt: it was encrypted with another login password." : "Could not decrypt: wrong passphrase.");
    return null;
  }
}
